/**
 * Form11_PatientResponsibilityForFeesPolicy.jsx
 * Collection: patient_responsibility_fees_v1
 * Redirect: /forms/12-policy-patient-responsibility-fees
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL_11 = "/api/forms/patient-responsibility-fees-v1";
const NEXT_11    = "/forms/12-policy-patient-responsibility-fees";

export function PatientResponsibilityForFeesV1({
  patientName="", patientAge="", patientGender="", practiceName="",
  mainPhone="", otherPhone="", onSubmit,
}) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    printName:patientName, responsibleParty:patientName,
    relationship:"Self", sigDate:new Date().toISOString().split("T")[0],
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const getSig=()=>(!sigRef.current||sigRef.current.isEmpty())?null:sigRef.current.toDataURL("image/png");

  const buildDoc=()=>({
    formType:"patient_responsibility_fees_v1",
    patient:{name:patientName, age:patientAge!==""?Number(patientAge):null, gender:patientGender},
    practiceName, mainPhone, otherPhone,
    printName:form.printName,
    authorization:{responsibleParty:form.responsibleParty,relationship:form.relationship,sigDate:form.sigDate||null,signatureDataURL:getSig()},
    meta:{submittedAt:new Date().toISOString(),formVersion:"1.0.0"},
  });

  const handleSubmit=async()=>{
    if(sigEmpty){showToast("Please sign before submitting.","error");return;}
    setSubmitting(true);
    try{
      const doc=buildDoc();
      const res=typeof onSubmit==="function"?await onSubmit(doc):await saveToMongo(doc,API_URL_11);
      if(res?._id)setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT_11),1800);
    }catch(e){console.error(e);showToast("Save failed.","error");}
    finally{setSubmitting(false);}
  };

  const handleDownload=async()=>{
    try{await downloadPDF(formRef,`fees-policy-v1-${patientName||"patient"}.pdf`);showToast("Downloaded!");}
    catch{showToast("PDF failed.","error");}
  };

  const practice=practiceName||"your practice";

  return (
    <FormShell title="Policy on Patient Responsibility for Fees"
      subtitle={`Thank you for choosing ${practice} for your care.`}
      dbLabel="MongoDB · patient_responsibility_fees_v1"
      patientName={patientName} patientAge={patientAge} patientGender={patientGender}
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">📋</span>Fee Responsibility Policy</div>
        <div className="wf-policy">
          <p>We believe that good care for you and your family starts with good communication. We have created this policy to help our patients understand the responsibilities that they and their families have for payment of our fees. If at any time you have questions or problems with our fees or payment process, please contact our billing department at <strong>{mainPhone||"our main phone"}</strong> or <strong>{otherPhone||"our alternate phone"}</strong>.</p>
          <p>We require that our patients promptly pay all charges that we present to them. We have taken into consideration and adjusted fees based on whether we participate in or accept insurance or government program payments, allowances, or limitations where applicable. If you are reimbursed directly by a program for the cost of your care, you must still pay our charges promptly, whether or not you have received that reimbursement.</p>
          <p>If you do not agree with patient responsibility amounts or reimbursement amounts set by your insurance or government program, this is a matter between you and that program. We are happy to provide you with factual information about your care and billing, but we still require you to promptly pay the entire charge we present to you.</p>
          <p>Payment for our services is due at the time that those services are provided. This includes copay amounts, program deductibles, earlier charges that remain unpaid, and charges for services not covered by insurance. We may also present charges via written statement or telephone calls following a visit.</p>
          <p><strong>Important:</strong> All payments outstanding after <strong>90 days</strong> from the date of services will be automatically referred to our collection agency. The collection agency charges are high — you will be responsible for an additional 50% of the amount owed or 100% of the collection fees, whichever is lower. Pay promptly to avoid these fees.</p>
          <p>It is your responsibility to present proper and up-to-date referral, insurance card, phone number, address, and photo ID to ensure prompt and accurate processing of your service charges.</p>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">✍</span>Acknowledgment</div>
        <Field label="Print Name">
          <input type="text" value={form.printName} onChange={e=>set("printName",e.target.value)} placeholder="Print patient name" />
        </Field>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

export default PatientResponsibilityForFeesV1;

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const FeesV1Schema = new Schema({
  formType:{type:String,default:"patient_responsibility_fees_v1"},
  patient:{name:String,age:Number,gender:String},
  practiceName:String, mainPhone:String, otherPhone:String, printName:String,
  authorization:{responsibleParty:String,relationship:String,sigDate:Date,signatureDataURL:String},
  meta:{submittedAt:Date,formVersion:String},
},{timestamps:true,collection:"patient_responsibility_fees_v1"});
*/
